// src/ApiPanel/pages/ca/MonthlyLedger.jsx
import React, { useState } from "react";
import {
  Card, DataTable, ExportButton, FilterBar, MonthField, Notice, ReportPage,
  Stat, StatGrid, count, currentPeriod, money, prettyDate, rupees,
} from "../../components/reports/ReportKit";
import { useReportQuery } from "../../hooks/useReportQuery";
import { apiPanelDownload, qs } from "../../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";

export default function MonthlyLedger() {
  const [period, setPeriod] = useState(currentPeriod);
  const [applied, setApplied] = useState({ period: currentPeriod() });
  const [exporting, setExporting] = useState(false);

  const { data, loading, error } = useReportQuery(
    API_PANEL_ENDPOINTS.CA_MONTHLY_LEDGER,
    applied
  );

  const summary = data?.summary || {};
  const rows = data?.data || [];

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiPanelDownload(
        `${API_PANEL_ENDPOINTS.CA_MONTHLY_LEDGER}${qs({ ...applied, export: "csv" })}`,
        `monthly_ledger_${applied.period}.csv`
      );
    } catch {
      /* nothing downloaded; page state unaffected */
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { key: "date", label: "Date", render: (r) => prettyDate(r.date) },
    { key: "txn_count", label: "Txns", numeric: true, render: (r) => count(r.txn_count) },
    {
      key: "success_count",
      label: "Success",
      numeric: true,
      render: (r) => <span className="apip-ok-txt">{count(r.success_count)}</span>,
    },
    {
      key: "failed_count",
      label: "Failed",
      numeric: true,
      render: (r) =>
        Number(r.failed_count) > 0 ? (
          <span className="apip-bad-txt">{count(r.failed_count)}</span>
        ) : (
          count(r.failed_count)
        ),
    },
    {
      key: "pending_count",
      label: "Pending",
      numeric: true,
      render: (r) =>
        Number(r.pending_count) > 0 ? (
          <span className="apip-warn-txt">{count(r.pending_count)}</span>
        ) : (
          count(r.pending_count)
        ),
    },
    { key: "amount", label: "Amount", numeric: true, render: (r) => money(r.amount) },
    { key: "commission", label: "Commission", numeric: true, render: (r) => money(r.commission) },
    { key: "tds", label: "TDS", numeric: true, render: (r) => money(r.tds) },
    { key: "charge", label: "Charge", numeric: true, render: (r) => money(r.charge) },
    { key: "gst", label: "GST", numeric: true, render: (r) => money(r.gst) },
    {
      key: "net_earning",
      label: "Net Earning",
      numeric: true,
      render: (r) => <strong>{money(r.net_earning)}</strong>,
    },
  ];

  const footer = [
    { value: "Totals" },
    { value: count(summary.total_txn), numeric: true },
    "", "", "",
    { value: money(summary.total_amount), numeric: true },
    { value: money(summary.total_commission), numeric: true },
    { value: money(summary.total_tds), numeric: true },
    { value: money(summary.total_charge), numeric: true },
    { value: money(summary.total_gst), numeric: true },
    { value: money(summary.net_earning), numeric: true },
  ];

  return (
    <ReportPage
      title="Monthly Ledger"
      subtitle="Day-by-day movement for one month, using the commission, TDS, charge and GST columns that belong to your own role."
      actions={<ExportButton onExport={handleExport} busy={exporting} />}
    >
      <Card>
        <FilterBar
          onApply={() => setApplied({ period })}
          onReset={() => {
            setPeriod(currentPeriod());
            setApplied({ period: currentPeriod() });
          }}
          busy={loading}
        >
          <MonthField label="Month" value={period} onChange={setPeriod} max={currentPeriod()} />
        </FilterBar>
      </Card>

      <StatGrid>
        <Stat label="Opening Balance" value={rupees(summary.opening_balance)} />
        <Stat label="Closing Balance" value={rupees(summary.closing_balance)} tone="brand" />
        <Stat label="Transactions" value={count(summary.total_txn)} />
        <Stat label="Volume" value={rupees(summary.total_amount)} />
        <Stat label="Commission" value={rupees(summary.total_commission)} />
        <Stat
          label="Net Earning"
          value={rupees(summary.net_earning)}
          tone="ok"
          sub="Commission less TDS, charge and GST"
        />
      </StatGrid>

      <Notice tone="info">
        Opening and closing balances come from the first and last ledger rows of the month, not
        from your wallet — the wallet shows where you are now, the ledger shows where you were
        then.
      </Notice>

      <Card title={`Daily movement${summary.role ? ` · ${summary.role}` : ""}`}>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          error={error}
          empty="No transactions in this month."
          footer={rows.length ? footer : null}
          rowKey={(r) => r.date}
        />
      </Card>
    </ReportPage>
  );
}
